import {
  MarketCountryCode,
  StockExchangeCodeType,
  SymbolAndOptionalExchangeCode,
  SymbolsAndExchangeData,
} from '../../models/fundamental/types';
import { GWMarketDataQueryBucket, GWMarketDataSummaryBucket } from '../../models/gateway/types';
import { MarketDataQueryService } from '../../services/market-data-query/MarketDataQuery.service';
import { ISSubscriptionService } from '../../services/subscription/ISSubscription.service';
import { MDSSubscriptionService } from '../../services/subscription/MDSSubscription.service';
import { MDSTSubscriptionService } from '../../services/subscription/MDSTSubscription.service';
import { NBBOSubscriptionService } from '../../services/subscription/NBBOSubscription.service';
import { SymbolStatusSubscriptionService } from '../../services/subscription/SymbolStatusSubscription.service';
import { isEmpty } from '../../util/typeGuards';

export const groupSymbolsByStockExchangeCode = (
  symbols: SymbolAndOptionalExchangeCode[],
): Record<StockExchangeCodeType, string[]> => symbols
  .reduce((accumulator: Record<StockExchangeCodeType, string[]>, item) => {
    const { symbol, stockExchangeCode } = item;
    // Use US_NASDAQ as default if stockExchangeCode is not provided
    const exchangeCode = stockExchangeCode || StockExchangeCodeType.US_NASDAQ;

    if (!accumulator[exchangeCode]) {
      accumulator[exchangeCode] = [];
    }
    accumulator[exchangeCode].push(symbol);
    return accumulator;
  }, {} as Record<StockExchangeCodeType, string[]>);

export const groupSymbolsByExchangeAndCountry = (
  symbols: SymbolAndOptionalExchangeCode[],
): Record<string, SymbolsAndExchangeData> => symbols.reduce((accumulator, item) => {
  const { symbol, stockExchangeCode, countryDesignation } = item;
  // Use US_NASDAQ as default if stockExchangeCode is not provided
  const exchangeCode = stockExchangeCode || StockExchangeCodeType.US_NASDAQ;
  const country = (countryDesignation as MarketCountryCode) || MarketCountryCode.UnitedStates;

  const key = `${exchangeCode}_${country}`;

  if (!accumulator[key]) {
    accumulator[key] = {
      symbols: [],
      stockExchangeCode: exchangeCode,
      countryDesignation: country,
    };
  }
  accumulator[key].symbols.push(symbol);
  return accumulator;
}, {} as Record<string, SymbolsAndExchangeData>);

export const chartSubscription = (
  symbolList: SymbolAndOptionalExchangeCode[],
  buckets: GWMarketDataSummaryBucket[],
  isUnsubscription: boolean = false,
  isBigChart: boolean = false,
): void => {
  if (isEmpty(symbolList)) {
    return;
  }

  const groupedSymbols = groupSymbolsByExchangeAndCountry(symbolList);
  const mdsService = MDSSubscriptionService.getInstance();

  // Make subscription requests by stockExchangeCode and countryDesignation
  Object.values(groupedSymbols).forEach(({ symbols: symbolsList, stockExchangeCode, countryDesignation }) => {
    // Handle all possible buckets
    buckets.forEach(bucket => {
      if (isUnsubscription) {
        mdsService.unsubscribeList(symbolsList, bucket, stockExchangeCode, countryDesignation, isBigChart);
      } else {
        mdsService.subscribeList(symbolsList, bucket, stockExchangeCode, countryDesignation, isBigChart);
      }
    });
  });
};

export const advancedChartSubscription = (
  symbolList: SymbolAndOptionalExchangeCode[],
  buckets: GWMarketDataQueryBucket[],
  startTime: string,
  endTime: string,
): void => {
  if (isEmpty(symbolList)) {
    return;
  }

  const groupedSymbols = groupSymbolsByExchangeAndCountry(symbolList);

  // Make subscription requests by stockExchangeCode and countryDesignation
  Object.values(groupedSymbols).forEach(({ symbols: symbolsList, stockExchangeCode, countryDesignation }) => {
    // Handle all possible buckets
    buckets.forEach(bucket => {
      MarketDataQueryService.getInstance().sendMarketDataQuery({
        symbols: symbolsList,
        bucket: bucket || GWMarketDataQueryBucket.OneDay,
        stockExchangeCode,
        endTime,
        startTime,
      });
    });
  });
};

export const marketDataSubscribeTradeSubscription = (
  symbols: SymbolAndOptionalExchangeCode[],
  isUnsubscription: boolean = false,
): void => {
  const symbolsByExchange = groupSymbolsByStockExchangeCode(symbols);
  const mdsTradeSubscriptionService = MDSTSubscriptionService.getInstance();

  // Make subscription requests by stockExchangeCode
  Object.entries(symbolsByExchange).forEach(([ stockExchangeCode, symbolsList ]) => {
    const exchangeCode = stockExchangeCode as StockExchangeCodeType;
    if (isUnsubscription) {
      mdsTradeSubscriptionService.unsubscribeList(symbolsList, exchangeCode);
    } else {
      mdsTradeSubscriptionService.subscribeList(symbolsList, exchangeCode);
    }
  });
};

export const instrumentSnapshotSubscription = (
  symbolList: SymbolAndOptionalExchangeCode[],
  isUnsubscription: boolean = false,
): void => {
  const symbolsByExchange = groupSymbolsByStockExchangeCode(symbolList);
  const isSubscriptionService = ISSubscriptionService.getInstance();

  // Make subscription requests by stockExchangeCode
  Object.entries(symbolsByExchange).forEach(([ stockExchangeCode, symbolsList ]) => {
    const exchangeCode = stockExchangeCode as StockExchangeCodeType;
    if (isUnsubscription) {
      isSubscriptionService.unsubscribeList(symbolsList, exchangeCode);
    } else {
      isSubscriptionService.subscribeList(symbolsList, exchangeCode);
    }
  });
};

export const nbboSubscription = (
  symbols: SymbolAndOptionalExchangeCode[],
  isUnsubscription: boolean = false,
): void => {
  const symbolsByExchange = groupSymbolsByStockExchangeCode(symbols);
  const nbboSubscriptionService = NBBOSubscriptionService.getInstance();

  // Make subscription requests by stockExchangeCode
  Object.entries(symbolsByExchange).forEach(([ stockExchangeCode, symbolsList ]) => {
    const exchangeCode = stockExchangeCode as StockExchangeCodeType;
    if (isUnsubscription) {
      nbboSubscriptionService.unsubscribeList(symbolsList, exchangeCode);
    } else {
      nbboSubscriptionService.subscribeList(symbolsList, exchangeCode);
    }
  });
};

export const symbolStatusSubscription = (
  symbols: SymbolAndOptionalExchangeCode[],
  isUnsubscription: boolean = false,
): void => {
  const symbolsByExchange = groupSymbolsByStockExchangeCode(symbols);
  const symbolStatusSubscriptionService = SymbolStatusSubscriptionService.getInstance();

  // Make subscription requests by stockExchangeCode
  Object.entries(symbolsByExchange).forEach(([ stockExchangeCode, symbolsList ]) => {
    const exchangeCode = stockExchangeCode as StockExchangeCodeType;
    if (isUnsubscription) {
      symbolStatusSubscriptionService.unsubscribeList(symbolsList, exchangeCode);
    } else {
      symbolStatusSubscriptionService.subscribeList(symbolsList, exchangeCode);
    }
  });
};
